import React, { useState } from 'react';
import { Container, Row, Col, Button, Form, Card, Row as BootstrapRow, Col as BootstrapCol } from 'react-bootstrap';
import { Table, Select } from 'antd';
import { useParams, useNavigate } from 'react-router-dom';
import { FilterOutlined, PlusOutlined } from '@ant-design/icons';
import PageTitle from '@/common/PageTitle/PageTitle';
import FormItem from '@/common/FormItem/FormItem';
import styles from './inventory-category-details.module.scss';

const products = [
  // KK Players
  {
    key: '1',
    image: 'https://images.unsplash.com/photo-1512436991641-6745cdb1723f?auto=format&fit=facearea&w=64&h=64',
    name: 'KrossKast Player 8',
    status: 'Active',
    stock: '12 in stock',
    category: 'KK Players',
    location: '3 stores',
  },
  {
    key: '2',
    image: 'https://images.unsplash.com/photo-1512436991641-6745cdb1723f?auto=format&fit=facearea&w=64&h=64',
    name: 'KrossKast Player 11',
    status: 'Active',
    stock: '10 in stock',
    category: 'KK Players',
    location: '4 stores',
  },
  // Bottoms
  {
    key: '3',
    image: 'https://images.unsplash.com/photo-1503342217505-b0a15ec3261c?auto=format&fit=facearea&w=64&h=64',
    name: 'Denim Shorts Women',
    status: 'Low in stock',
    stock: '1 in stock',
    category: 'Bottoms',
    location: '1 store',
  },
  {
    key: '4',
    image: 'https://images.unsplash.com/photo-1511367461989-f85a21fda167?auto=format&fit=facearea&w=64&h=64',
    name: 'Slim Fit Jeans Blue',
    status: 'Active',
    stock: '15 in stock',
    category: 'Bottoms',
    location: '5 stores',
  },
  // Coats
  {
    key: '5',
    image: 'https://images.unsplash.com/photo-1544966503-7cc5ac882d5f?auto=format&fit=facearea&w=64&h=64',
    name: 'Winter Coat Black',
    status: 'Active',
    stock: '8 in stock',
    category: 'Coats',
    location: '2 stores',
  },
  {
    key: '6',
    image: 'https://images.unsplash.com/photo-1544966503-7cc5ac882d5f?auto=format&fit=facearea&w=64&h=64',
    name: 'Wool Coat Brown',
    status: 'Active',
    stock: '5 in stock',
    category: 'Coats',
    location: '3 stores',
  },
  // Jeans
  {
    key: '7',
    image: 'https://images.unsplash.com/photo-1542272604-787c3835535d?auto=format&fit=facearea&w=64&h=64',
    name: 'Bootcut Jeans Dark',
    status: 'Active',
    stock: '12 in stock',
    category: 'Jeans',
    location: '4 stores',
  },
  {
    key: '8',
    image: 'https://images.unsplash.com/photo-1542272604-787c3835535d?auto=format&fit=facearea&w=64&h=64',
    name: 'Skinny Jeans Black',
    status: 'Low in stock',
    stock: '2 in stock',
    category: 'Jeans',
    location: '2 stores',
  },
  // Tops
  {
    key: '9',
    image: 'https://images.unsplash.com/photo-1511367461989-f85a21fda167?auto=format&fit=facearea&w=64&h=64',
    name: 'Tank Top White',
    status: 'Active',
    stock: '28 in stock',
    category: 'Tops',
    location: '3 stores',
  },
  // Accessories
  {
    key: '10',
    image: 'https://images.unsplash.com/photo-1511367461989-f85a21fda167?auto=format&fit=facearea&w=64&h=64',
    name: 'Unisex Socks Black',
    status: 'Active',
    stock: '37 in stock',
    category: 'Accessories',
    location: '2 stores',
  },
  {
    key: '11',
    image: 'https://images.unsplash.com/photo-1512436991641-6745cdb1723f?auto=format&fit=facearea&w=64&h=64',
    name: 'Sunglasses',
    status: 'Active',
    stock: '20 in stock',
    category: 'Accessories',
    location: '6 stores',
  },
];

const CategoryDetails: React.FC = () => {
  const { categoryName } = useParams();
  const navigate = useNavigate();
  
  // Filter state
  const [filters, setFilters] = useState({
    productName: undefined as string | undefined,
    stockInfo: undefined as string | undefined,
    status: undefined as string | undefined,
  });
  const [resetCounter, setResetCounter] = useState(0);

  // Normalize for case-insensitive match and possible plural/singular
  const normalizedCategory = (categoryName || '').toLowerCase().replace(/s$/, '');
  const categoryProducts = products.filter(
    (p) => (p.category || '').toLowerCase().replace(/s$/, '') === normalizedCategory
  );

  // Filter options
  const filterOptions = {
    productNames: categoryProducts.map(product => product.name),
    stockInfos: [...new Set(categoryProducts.map(product => product.stock))],
    statuses: [...new Set(categoryProducts.map(product => product.status))],
  };

  // Filtering logic
  const filteredProducts = categoryProducts.filter(product => {
    return (
      (!filters.productName || product.name === filters.productName) &&
      (!filters.stockInfo || product.stock === filters.stockInfo) &&
      (!filters.status || product.status === filters.status)
    );
  });

  // Filter change handlers
  const handleFilterChange = (key: string, value: string | undefined) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleReset = () => {
    setFilters({ productName: undefined, stockInfo: undefined, status: undefined });
    setResetCounter(prev => prev + 1);
  };

  // Define columns for Ant Design Table
  const columns = [
    {
      title: '',
      dataIndex: 'checkbox',
      width: 20,
      align: 'center' as const,
      className: 'checkbox-col',
      render: (_: any, record: any) => (
        <input
          type="checkbox"
          className={styles.checkbox}
          style={{ margin: 0 }}
        />
      ),
    },
    {
      title: 'Name of product',
      dataIndex: 'name',
      render: (_: any, record: any) => (
        <span className={styles.productCell}>
          <img src={record.image} alt={record.name} className={styles.productImage} />
          <span className={styles.productName}>{record.name}</span>
        </span>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (status: string) => {
        const statusClass = status === 'Active' ? styles.active :
                           status === 'Sold out' ? styles.soldOut :
                           styles.lowStock;
        return (
          <span className={`${styles.statusBadge} ${statusClass}`}>
            {status}
          </span>
        );
      },
    },
    {
      title: 'Stock info',
      dataIndex: 'stock',
      render: (stock: string) => <span className={styles.stockInfo}>{stock}</span>,
    },
    {
      title: 'Category',
      dataIndex: 'category',
      render: (category: string) => <span className={styles.categoryInfo}>{category}</span>,
    },
    {
      title: 'Location',
      dataIndex: 'location',
      render: (location: string) => <span className={styles.locationInfo}>{location}</span>,
    },
  ];

  return (
    <Container fluid>
      <Card className={styles.card}>
        <Card.Body>
          <Card.Title style={{ display: 'flex', justifyContent: 'space-between' }}>
            <PageTitle title={`Category - ${categoryName}`} />
            <Button 
              onClick={handleReset}
              style={{ 
                background: 'white', 
                color: '#333', 
                border: '1px solid #e0e0e0', 
                borderRadius: '6px',
                padding: '8px 16px',
                fontWeight: '500'
              }}
            >
              <FilterOutlined /> Reset All
            </Button>
          </Card.Title>
          
          <div className={styles.lastUpdate}>
            Last update January 29, 2023 at 2:39PM
          </div>
          
          <div className={styles.form}>
            <BootstrapRow>
              <BootstrapCol>
                <BootstrapRow className="mb-2">
                  <BootstrapCol>
                    <BootstrapRow>
                      <BootstrapCol md={4}>
                        <FormItem
                          label="Name of Product"
                          className="customSelectWrapper"
                          layout="vertical"
                        >
                          <Select
                            key={`productName-reset-${resetCounter}`}
                            showSearch
                            allowClear
                            className={`customSelect ${styles.selectField}`}
                            placeholder="Any product name"
                            value={filters.productName}
                            onChange={value => handleFilterChange('productName', value)}
                            options={filterOptions.productNames.map(name => ({ label: name, value: name }))}
                          />
                        </FormItem>
                      </BootstrapCol>
                      <BootstrapCol md={4}>
                        <FormItem
                          label="Stock Info"
                          className="customSelectWrapper"
                          layout="vertical"
                        >
                          <Select
                            key={`stockInfo-reset-${resetCounter}`}
                            showSearch
                            allowClear
                            className={`customSelect ${styles.selectField}`}
                            placeholder="Any stock info"
                            value={filters.stockInfo}
                            onChange={value => handleFilterChange('stockInfo', value)}
                            options={filterOptions.stockInfos.map(stock => ({ label: stock, value: stock }))}
                          />
                        </FormItem>
                      </BootstrapCol>
                      <BootstrapCol md={4}>
                        <FormItem
                          label="Status"
                          className="customSelectWrapper"
                          layout="vertical"
                        >
                          <Select
                            key={`status-reset-${resetCounter}`}
                            showSearch
                            allowClear
                            className={`customSelect ${styles.selectField}`}
                            placeholder="Any status"
                            value={filters.status}
                            onChange={value => handleFilterChange('status', value)}
                            options={filterOptions.statuses.map(status => ({ label: status, value: status }))}
                          />
                        </FormItem>
                      </BootstrapCol>
                    </BootstrapRow>
                  </BootstrapCol>
                </BootstrapRow>
                <BootstrapRow className="mb-2">
                  <BootstrapCol>
                    <BootstrapRow>
                      <BootstrapCol 
                        md={12}
                        className="d-flex justify-content-end"
                      >
                        <Button
                          style={{ 
                            background: 'white', 
                            color: '#e21e26', 
                            border: '1px solid #e21e26', 
                            borderRadius: '6px',
                            padding: '8px 16px',
                            fontWeight: '500',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            justifyContent: 'center',
                            height: '50px',
                            marginTop: '8px'
                          }}
                          onClick={() => navigate('/inventory-add-products')}
                        >
                          <PlusOutlined style={{ fontSize: 16 }} />
                          Add New Product
                        </Button>
                      </BootstrapCol>
                    </BootstrapRow>
                  </BootstrapCol>
                </BootstrapRow>
              </BootstrapCol>
            </BootstrapRow>
          </div>
          
          {/* Products Table */}
          <Table
            className={`${styles.table} ${styles.legacyRowHeightTable}`}
            columns={columns}
            dataSource={filteredProducts}
            pagination={false}
            scroll={{ x: true }}
            rowKey="key"
            bordered
            onRow={(record) => ({
              onClick: () => navigate(`/inventory-products/${record.key}`),
              className: styles.clickableRow,
            })}
            components={{
              header: {
                cell: (props: any) => (
                  <th
                    {...props}
                    className={styles.tableHeader}
                  />
                )
              }
            }}
          />
        </Card.Body>
      </Card>
    </Container>
  );
};

export default CategoryDetails; 